/**
 * 字体回退（DESIGN.md §2.5，用户反馈"选了英文字体、打中文，变成方框"后
 * 新增）。单一字体的字符集永远有限——选中一款不含某个字符字形的字体，
 * HarfBuzz 会老实shaping 出这个字体自己的 `.notdef`（glyph 0），多数字体
 * 这个字形本身就是一个实心/空心的方框轮廓，不是渲染出错，是字体确实
 * 没有这个字。真实排版引擎（含浏览器、Photoshop）都会在缺字时按顺序
 * 换一款已加载的字体接着排，这里补上同样的行为。
 *
 * 实现思路：按字符（不是按 grapheme cluster——变体选择符/组合字符这类
 * 复杂场景不在这次范围内，是已知简化）逐个问"主字体有没有这个字"，
 * 没有就依次问候选字体，谁先有就用谁；把文本切成"同一款字体负责"的
 * 连续区间，每段各自单独 shape，再拼回一份 `ShapeRun`。
 *
 * 不同字体的 upem（每 em 单位数）可能不同（内置的 Inter=2048，
 * JetBrains Mono/思源黑体=1000，实测验证过），下游只认**一个**外层缩放
 * 因子（`fontSize/upem`），候选字体在传进来之前必须已经用
 * `TextShaper#rescaleTo` 换算到主字体的 upem 空间（这里不做，调用方
 * `FontLibrary.shaperFor` 负责）。
 *
 * glyph id 需要额外记住"来自哪个字体"才能在 `glyphPath()` 时调对字体——
 * 不引入新的数据结构（`ShapedGlyph.glyphId` 保持纯 `number`，
 * `layout.ts`/`raster.ts` 零改动），改用打包编码：OpenType/TrueType 的
 * glyph 索引规范上限定是 16 位无符号整数（≤65535），把"字体来源下标"
 * 编进高位、原始 glyph id 编进低 16 位绝对不会跟真实 glyph id 冲突。
 */
import type { Shaper, ShapedGlyph, ShapeRun, TextShaper } from './shaper';

const GLYPH_ID_BITS = 16;
const GLYPH_ID_MASK = (1 << GLYPH_ID_BITS) - 1;

function packGlyphId(shaperIndex: number, rawGlyphId: number): number {
  return (shaperIndex << GLYPH_ID_BITS) | rawGlyphId;
}

function unpackGlyphId(packed: number): { readonly shaperIndex: number; readonly rawGlyphId: number } {
  return { shaperIndex: packed >>> GLYPH_ID_BITS, rawGlyphId: packed & GLYPH_ID_MASK };
}

/**
 * 组合了回退候选的 shaper——对 `layout.ts`/`raster.ts` 而言就是一个普通
 * `Shaper`，感知不到背后其实按字符拆给了不同字体。
 */
export class FallbackTextShaper implements Shaper {
  readonly upem: number;
  readonly #primary: TextShaper;
  /** 候选字体，按优先级顺序——已经 `rescaleTo(primary.upem)` 过。 */
  readonly #candidates: readonly TextShaper[];

  constructor(primary: TextShaper, candidates: readonly TextShaper[]) {
    this.upem = primary.upem;
    this.#primary = primary;
    this.#candidates = candidates;
  }

  /** 0 = 主字体，1..N = 候选字体（下标 + 1）。谁都没有这个字形就还是主字体——原样出它自己的缺字占位符，不会比不做回退更差。 */
  #pickShaperIndex(codePoint: number): number {
    if (this.#primary.hasGlyph(codePoint)) return 0;
    for (let i = 0; i < this.#candidates.length; i++) {
      if (this.#candidates[i]!.hasGlyph(codePoint)) return i + 1;
    }
    return 0;
  }

  #shaperAt(index: number): TextShaper {
    return index === 0 ? this.#primary : this.#candidates[index - 1]!;
  }

  shape(text: string, vertical = false): ShapeRun {
    const chars = [...text];
    if (chars.length === 0) return { glyphs: [], upem: this.upem };

    const assignment = chars.map((ch) => this.#pickShaperIndex(ch.codePointAt(0) ?? 0));
    const glyphs: ShapedGlyph[] = [];
    let i = 0;
    while (i < chars.length) {
      const shaperIndex = assignment[i]!;
      let j = i + 1;
      while (j < chars.length && assignment[j] === shaperIndex) j++;
      const runText = chars.slice(i, j).join('');
      const run = this.#shaperAt(shaperIndex).shape(runText, vertical);
      for (const g of run.glyphs) {
        glyphs.push({
          ...g,
          glyphId: packGlyphId(shaperIndex, g.glyphId),
          // run 内部的 cluster 是相对 runText 的下标，+i 换算回原文里的位置
          cluster: g.cluster + i,
        });
      }
      i = j;
    }
    return { glyphs, upem: this.upem };
  }

  glyphPath(packedGlyphId: number): string {
    const { shaperIndex, rawGlyphId } = unpackGlyphId(packedGlyphId);
    const shaper = shaperIndex === 0 ? this.#primary : this.#candidates[shaperIndex - 1];
    if (shaper === undefined) return '';
    return shaper.glyphPath(rawGlyphId);
  }

  destroy(): void {
    this.#primary.destroy();
    for (const candidate of this.#candidates) candidate.destroy();
  }
}
